import { AlertTriangle, Check, Clock, PauseCircle } from 'lucide-react';
import type { TicketSummary } from '@aurora/contracts';
import { cn } from '../../lib/cn';
import { absoluteTime } from '../../lib/format';
import { Badge } from '../../components/ui';
import { useNow } from './sla-now';

/**
 * What a ticket's SLA means right now, in one short phrase.
 *
 * Built on `sla_due_at` — the nearest deadline whose timer is still running —
 * and never on the stored due dates. The first-response due date stays on a
 * ticket after the desk has replied, and the list used to take the earlier of
 * that and the resolution date: once the reply's deadline had passed, a ticket
 * answered within the hour read "3h overdue" in red for the rest of its life.
 * A met target has no running timer, so it can no longer be shown as late.
 *
 * Shared on purpose: the dashboard uses it, and the ticket page can. One
 * component means "at risk" looks the same on every screen.
 */

export type SlaTicket = Pick<TicketSummary, 'sla_state' | 'sla_due_at' | 'sla_due_target'>;

export type SlaKind = 'none' | 'met' | 'paused' | 'breached' | 'overdue' | 'at_risk' | 'soon' | 'on_track';

export interface SlaDescription {
  kind: SlaKind;
  /** What the cell shows. */
  label: string;
  /** A whole sentence, for the tooltip and for screen readers. */
  description: string;
  dueAt: string | null;
}

const TARGETS: Record<NonNullable<SlaTicket['sla_due_target']>, { verb: string; noun: string }> = {
  first_response: { verb: 'respond', noun: 'First response' },
  next_response: { verb: 'reply', noun: 'Reply' },
  resolution: { verb: 'resolve', noun: 'Resolution' },
};

/** Two hours: roughly when an agent should start reshuffling their queue. */
const SOON_MS = 2 * 3_600_000;

/** "45m", "3h 20m", "2d 4h", "6d" — precise when it is close, coarse when it is not. */
export function formatSpan(ms: number): string {
  const minutes = Math.floor(Math.abs(ms) / 60_000);
  if (minutes < 60) return `${Math.max(minutes, 1)}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    return hours < 10 && rest > 0 ? `${hours}h ${rest}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  return days < 3 && rest > 0 ? `${days}d ${rest}h` : `${days}d`;
}

export function describeSla(ticket: SlaTicket, now: number = Date.now()): SlaDescription {
  const dueAt = ticket.sla_due_at;
  const target = ticket.sla_due_target ? TARGETS[ticket.sla_due_target] : null;
  const when = dueAt ? absoluteTime(dueAt) : '';

  switch (ticket.sla_state) {
    case 'none':
      return { kind: 'none', label: '—', description: 'No SLA applies to this ticket.', dueAt: null };
    case 'met':
      return { kind: 'met', label: 'Met', description: 'Every SLA target on this ticket was met.', dueAt: null };
    case 'paused':
      return {
        kind: 'paused',
        label: 'Paused',
        description: 'The SLA clock is paused while the ticket waits.',
        dueAt: null,
      };
    case 'breached':
      return {
        kind: 'breached',
        label: 'Breached',
        description:
          dueAt && target
            ? `An SLA target was missed. ${target.noun} is due ${when}.`
            : 'An SLA target on this ticket was missed.',
        dueAt,
      };
    default:
      break;
  }

  // Running: on track or at risk.
  if (!dueAt) {
    return ticket.sla_state === 'at_risk'
      ? { kind: 'at_risk', label: 'At risk', description: 'At risk of missing an SLA target.', dueAt: null }
      : { kind: 'on_track', label: 'On track', description: 'Within its SLA targets.', dueAt: null };
  }

  const delta = new Date(dueAt).getTime() - now;
  const noun = target?.noun ?? 'The next SLA target';

  // A running timer past its deadline that the breach job has not reached yet.
  // It is late now, whatever the stored state still says.
  if (delta < 0) {
    return {
      kind: 'overdue',
      label: `${formatSpan(delta)} overdue`,
      description: `${noun} was due ${when}.`,
      dueAt,
    };
  }

  const label = delta < 60_000 ? 'Due now' : target ? `${formatSpan(delta)} to ${target.verb}` : `Due in ${formatSpan(delta)}`;

  if (ticket.sla_state === 'at_risk') {
    return { kind: 'at_risk', label, description: `At risk: ${noun.toLowerCase()} is due ${when}.`, dueAt };
  }
  return {
    kind: delta < SOON_MS ? 'soon' : 'on_track',
    label,
    description: `${noun} is due ${when}.`,
    dueAt,
  };
}

/**
 * The SLA chip.
 *
 * A breach is not a colour change — it is an icon, a word and a colour,
 * because missing a breach is the expensive failure here. Re-renders every
 * thirty seconds, so a countdown on a list nobody touches keeps counting.
 */
export function SlaDeadline({ ticket, className }: { ticket: SlaTicket; className?: string }) {
  const now = useNow();
  const sla = describeSla(ticket, now);

  return (
    <span title={sla.description} className={cn('inline-flex max-w-full items-center', className)} data-sla={sla.kind}>
      <span aria-hidden className="inline-flex max-w-full min-w-0 items-center">
        <SlaVisual sla={sla} />
      </span>
      <span className="sr-only">{sla.kind === 'none' ? sla.description : `${sla.label}. ${sla.description}`}</span>
    </span>
  );
}

function SlaVisual({ sla }: { sla: SlaDescription }) {
  switch (sla.kind) {
    case 'none':
      return <span className="text-mini text-ink-subtle">—</span>;
    case 'met':
      return (
        <Badge tone="positive">
          <Check className="size-3" />
          {sla.label}
        </Badge>
      );
    case 'paused':
      return (
        <Badge tone="neutral">
          <PauseCircle className="size-3" />
          {sla.label}
        </Badge>
      );
    case 'breached':
    case 'overdue':
      return (
        <Badge tone="critical" className="max-w-full font-semibold">
          <AlertTriangle className="size-3 shrink-0" />
          <span className="truncate">{sla.label}</span>
        </Badge>
      );
    case 'at_risk':
      return (
        <Badge tone="caution" className="max-w-full">
          <Clock className="size-3 shrink-0" />
          <span className="truncate">{sla.label}</span>
        </Badge>
      );
    case 'soon':
      return (
        <span className="inline-flex min-w-0 items-center gap-1 text-mini font-medium text-caution-ink">
          <Clock className="size-3 shrink-0" />
          <span className="truncate">{sla.label}</span>
        </span>
      );
    default:
      return <span className="truncate text-mini text-ink-muted">{sla.label}</span>;
  }
}
